import * as React from "react";
import { Badge } from "@/components/ui/badge";
import { UnifiedParametersGroup } from "@/components/model-management/detail/unified-parameters-group";
import type { AdminParameterOverride } from "@genieartinc/schemas";
import { detectParameterGroup, getMainOutputParam } from "@genieartinc/schemas";
import type { ParameterInfo } from "@/components/model-management/detail/collapsible-parameter-row";

type JsonValue = string | number | boolean | null | JsonValue[] | { [key: string]: JsonValue };

export interface SchemaProperty {
  type?: string;
  title?: string;
  description?: string;
  default?: JsonValue;
  minimum?: number;
  maximum?: number;
  enum?: JsonValue[];
  [key: string]: JsonValue | undefined;
}

interface ParametersTabProps {
  model: {
    parameterSchema?: {
      properties?: Record<string, SchemaProperty>;
      required?: string[];
    };
  };
  parameterOverrides: Record<string, AdminParameterOverride | JsonValue>;
  onOverridesChange: (overrides: Record<string, AdminParameterOverride | JsonValue>) => void;
}

export function ParametersTab({ model, parameterOverrides, onOverridesChange }: ParametersTabProps) {
  // Convert to unified parameter structure
  const parameters = React.useMemo(() => {
    if (!model.parameterSchema?.properties) {
      return {};
    }

    const result: Record<string, { info: ParameterInfo; override?: AdminParameterOverride; group: "input" | "output" | "settings" }> = {};

    for (const [paramName, paramSchema] of Object.entries(model.parameterSchema.properties)) {
      const override = parameterOverrides[paramName] as AdminParameterOverride | undefined;
      const group = override ? detectParameterGroup(override) : "settings";

      const paramInfo: ParameterInfo = {
        name: paramName,
        type: paramSchema.type || "string",
        title: paramSchema.title,
        description: paramSchema.description,
        default: paramSchema.default,
        minimum: paramSchema.minimum,
        maximum: paramSchema.maximum,
        enum: paramSchema.enum,
        required: model.parameterSchema!.required?.includes(paramName),
      };

      result[paramName] = { info: paramInfo, override, group };
    }

    return result;
  }, [model.parameterSchema, parameterOverrides]);

  // Get main output param
  const mainOutputParam = React.useMemo(() => {
    const overrides: Record<string, AdminParameterOverride> = {};
    for (const [key, value] of Object.entries(parameterOverrides)) {
      if (key.startsWith("__")) continue;
      if (typeof value === "object" && value !== null && !Array.isArray(value)) {
        overrides[key] = value as AdminParameterOverride;
      }
    }
    return getMainOutputParam(overrides);
  }, [parameterOverrides]);

  // Extract saved order from special keys
  const savedOrder = React.useMemo(() => {
    return {
      input: Array.isArray(parameterOverrides.__inputOrder) ? parameterOverrides.__inputOrder as string[] : undefined,
      output: Array.isArray(parameterOverrides.__outputOrder) ? parameterOverrides.__outputOrder as string[] : undefined,
      settings: Array.isArray(parameterOverrides.__settingsOrder) ? parameterOverrides.__settingsOrder as string[] : undefined,
    };
  }, [parameterOverrides]);

  const handleParametersChange = (updates: Record<string, AdminParameterOverride | string[]>) => {
    const newOverrides = { ...parameterOverrides };

    for (const [paramName, override] of Object.entries(updates)) {
      newOverrides[paramName] = override;
    }

    onOverridesChange(newOverrides);
  };

  const handleGroupChange = (paramName: string, newGroup: "input" | "output" | "settings") => {
    const override = parameterOverrides[paramName] as AdminParameterOverride | undefined;
    const updates: Record<string, AdminParameterOverride> = {};

    if (newGroup === "input") {
      updates[paramName] = { ...override, group: "input", isInput: true, isOutput: false };
    } else if (newGroup === "output") {
      updates[paramName] = { ...override, group: "output", isOutput: true, isInput: false };
    } else {
      updates[paramName] = { ...override, group: "settings", isInput: false, isOutput: false };
    }

    handleParametersChange(updates);
  };

  const totalParams = Object.keys(parameters).length;
  const overrideCount = Object.keys(parameterOverrides).filter(
    (key) => !key.startsWith("__")
  ).length;

  if (!model.parameterSchema?.properties) {
    return (
      <div className="rounded-lg border p-6 text-center mt-3">
        <p className="text-xs text-muted-foreground">No parameter schema available</p>
        <p className="text-[11px] text-muted-foreground mt-0.5">
          Run model sync to fetch parameter schema from provider
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6 mt-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold">Parameter Groups</h3>
          <p className="text-xs text-muted-foreground">
            Drag parameters between groups to organize into input handles, output params, and node settings
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-xs">
            {totalParams} total
          </Badge>
          {overrideCount > 0 && (
            <Badge variant="secondary" className="text-xs">
              {overrideCount} configured
            </Badge>
          )}
        </div>
      </div>

      {/* Unified Parameters Group (with cross-group DND) */}
      <UnifiedParametersGroup
        parameters={parameters}
        mainOutputParam={mainOutputParam}
        savedOrder={savedOrder}
        onParametersChange={handleParametersChange}
        onGroupChange={handleGroupChange}
      />
    </div>
  );
}
